import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * "Edit in StarScribe ▾" (picture 31, the owner's pick A): first in an answer's row, a red button.
 *
 * - Its main part makes a page from the answer (POST /v1/starscribe/pages with `fromAnswer`) and opens it beside the chat.
 * - Its ▾ opens "ADD TO RECENT PAGE": up to three of the person's own pages, newest first, each with its age. The one picked
 *   gets the answer at its end (POST .../append, one change in the person's name) and opens.
 *
 * Either way the chat gets the page's card (PageCard.tsx). A refusal is said beside the button, never swallowed. The ▾ is a
 * WAI-ARIA menu button, as the header's "More actions" is (HeaderOverflowMenu.tsx): Enter, Space or Down opens it, Up opens
 * it on its last page, the arrows, Home and End move, Escape closes it and returns to its button, Tab closes it.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { usePopoverPlacement } from '../popoverPlacement.js';
import { ChevronDown, PenLine } from '../starscribeIcons.js';
import { pagePath, parsePageRow, parsePageRows, refusalText } from './pages.js';
import { pageAge } from './words.js';
/** The menu's width where there is room (picture 31). */
const MENU_WIDTH = 310;
/** How many recent pages the menu lists (picture 31). */
export const RECENT_PAGES = 3;
function menuItems(menu) {
    return Array.from(menu?.querySelectorAll('[role="menuitem"]') ?? []);
}
export function EditInStarScribe({ request, answer, conversationId, onPage, nowMs = Date.now }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(false);
    const [recent, setRecent] = useState({ state: 'loading' });
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const busyRef = useRef(false);
    const liveRef = useRef(true);
    const menuId = useId();
    const menuOpen = open !== false;
    useEffect(() => { liveRef.current = true; return () => { liveRef.current = false; }; }, []);
    const style = usePopoverPlacement(menuOpen, rootRef, menuRef, rootRef, { align: 'start', side: 'below', width: MENU_WIDTH, gap: 6 });
    const chat = conversationId ? { conversationId } : {};
    /** One request at a time from this button: the page it names opens, a refusal is said beside the button. */
    const run = async (work, fallback) => {
        if (busyRef.current)
            return;
        busyRef.current = true;
        setBusy(true);
        setError('');
        try {
            const page = await work();
            if (!liveRef.current)
                return;
            if (page)
                onPage(page);
            else
                setError(fallback);
        }
        catch (failure) {
            if (liveRef.current)
                setError(refusalText(failure, fallback));
        }
        finally {
            busyRef.current = false;
            if (liveRef.current)
                setBusy(false);
        }
    };
    const create = () => void run(async () => {
        const answered = await request('/v1/starscribe/pages', { method: 'POST', body: { fromAnswer: { ...chat, text: answer } } });
        const page = parsePageRow(answered?.page);
        return page ? { pageId: page.pageId, title: page.title, version: page.version ?? 1 } : null;
    }, 'StarScribe could not make a page from this answer. Try again.');
    const append = (row) => void run(async () => {
        const answered = await request(pagePath(row.pageId, 'append'), { method: 'POST', body: { markdown: answer, ...chat } });
        const version = typeof answered?.version === 'number' && Number.isSafeInteger(answered.version) && answered.version > 0 ? answered.version : row.version ?? 1;
        return { pageId: row.pageId, title: row.title, version };
    }, `StarScribe could not add this answer to ${row.title}. Try again.`);
    // Opened: the person's own pages, newest first, read again each time (one of them may have changed since).
    useEffect(() => {
        if (!menuOpen)
            return undefined;
        const controller = new AbortController();
        setRecent({ state: 'loading' });
        request('/v1/starscribe/pages', { query: { which: 'mine' }, signal: controller.signal }).then((raw) => { if (!controller.signal.aborted)
            setRecent({ state: 'ready', pages: parsePageRows(raw).slice(0, RECENT_PAGES) }); }, (failure) => { if (!controller.signal.aborted)
            setRecent({ state: 'error', message: refusalText(failure, 'StarScribe could not list your pages.') }); });
        return () => controller.abort();
    }, [menuOpen, request]);
    // Opened: the menu takes focus at once, and its first page (or its last, from Up) once the list is there.
    useLayoutEffect(() => {
        if (!menuOpen)
            return;
        const items = menuItems(menuRef.current);
        const target = open === 'last' ? items[items.length - 1] : items[0];
        const active = menuRef.current?.ownerDocument.activeElement;
        if (target && (active === menuRef.current || active === triggerRef.current || !menuRef.current?.contains(active ?? null)))
            target.focus();
        else if (!target && active !== menuRef.current)
            menuRef.current?.focus();
    }, [menuOpen, open, recent]);
    // A press outside closes it.
    useEffect(() => {
        if (!menuOpen)
            return undefined;
        const outside = (event) => {
            const root = rootRef.current;
            if (root && !event.composedPath().includes(root))
                setOpen(false);
        };
        document.addEventListener('pointerdown', outside, true);
        return () => document.removeEventListener('pointerdown', outside, true);
    }, [menuOpen]);
    const close = (refocus) => {
        setOpen(false);
        if (refocus)
            triggerRef.current?.focus();
    };
    const menuKey = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close(true);
            return;
        }
        if (event.key === 'Tab') {
            event.stopPropagation();
            close(true);
            return;
        }
        const items = menuItems(menuRef.current);
        if (!items.length)
            return;
        const current = items.indexOf(event.target);
        const next = event.key === 'ArrowDown' ? (current + 1) % items.length
            : event.key === 'ArrowUp' ? (current - 1 + items.length) % items.length
                : event.key === 'Home' ? 0
                    : event.key === 'End' ? items.length - 1
                        : -1;
        if (next < 0)
            return;
        event.preventDefault();
        event.stopPropagation();
        items[next]?.focus();
    };
    return _jsxs("span", { className: "gyro-ss-edit-wrap", children: [_jsxs("span", { className: "gyro-ss-edit", ref: rootRef, children: [_jsxs("button", { type: "button", className: "gyro-ss-edit-main", onClick: () => { setOpen(false); create(); }, disabled: busy, "aria-busy": busy || undefined, children: [_jsx(PenLine, { size: 12, "aria-hidden": "true" }), _jsx("span", { children: "Edit in StarScribe" })] }), _jsx("button", { ref: triggerRef, type: "button", className: "gyro-ss-edit-more", "aria-label": "Add to a recent page", title: "Add to a recent page", "aria-haspopup": "menu", "aria-expanded": menuOpen, "aria-controls": menuOpen ? menuId : undefined, disabled: busy, onClick: () => setOpen((current) => current ? false : 'first'), onKeyDown: (event) => {
                            if (event.key === 'Escape' && menuOpen) {
                                event.preventDefault();
                                event.stopPropagation();
                                close(true);
                                return;
                            }
                            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')
                                return;
                            event.preventDefault();
                            event.stopPropagation();
                            setOpen(event.key === 'ArrowUp' ? 'last' : 'first');
                        }, children: _jsx(ChevronDown, { size: 12, "aria-hidden": "true" }) }), menuOpen && _jsxs("span", { ref: menuRef, id: menuId, role: "menu", "aria-label": "Add to recent page", className: "gyro-ss-menu", tabIndex: -1, style: style ?? undefined, onKeyDown: menuKey, children: [_jsx("span", { className: "gyro-ss-menu-group", "aria-hidden": "true", children: "Add to recent page" }), recent.state === 'loading' && _jsx("span", { className: "gyro-ss-menu-note", role: "status", children: "Loading your pages\u2026" }), recent.state === 'error' && _jsx("span", { className: "gyro-ss-menu-note", role: "alert", children: recent.message }), recent.state === 'ready' && recent.pages.length === 0 && _jsx("span", { className: "gyro-ss-menu-note", children: "You have no pages yet." }), recent.state === 'ready' && recent.pages.map((row) => _jsxs("button", { type: "button", role: "menuitem", tabIndex: -1, className: "gyro-ss-menu-item", onClick: () => { close(true); append(row); }, children: [_jsx("span", { className: "gyro-ss-menu-title", children: row.title }), _jsx("span", { className: "gyro-ss-menu-age", children: pageAge(row.updatedAtMs, nowMs()) })] }, row.pageId))] })] }), error && _jsx("span", { className: "gyro-ss-edit-error", role: "alert", children: error })] });
}
//# sourceMappingURL=EditInStarScribe.js.map